import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import ContractMarkdown from '../components/ContractMarkdown';
import SignatureModal from '../components/SignatureModal';
import { COMPANY } from '../config/brand';
import Logo from '../components/Logo';

// Candidate signing page: /sign/:reference. Public; the reference in the link
// is the bearer. Everything shown comes from the server (terms, the contract
// text the PDF is made from, the consent wording) — nothing is hardcoded, so
// the page can't promise terms the contract doesn't contain.

const ADDRESS_GAP = '[your address — to be completed]';
const DATE_GAP = '[date of signing — to be completed]';

const sentence = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
const fmtMoney = (v) => {
  const n = Number(String(v || '').replace(/[,\s]/g, ''));
  return n > 0 ? `AUD ${n.toLocaleString('en-AU')}` : '';
};

function downloadBase64Pdf(base64, filename) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'signed-contract.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-cream">
    <header className="border-b border-line bg-white">
      <div className="wc-container flex h-14 items-center justify-between">
        <Logo className="h-8" />
        <span className="text-xs text-muted">Employment contract</span>
      </div>
    </header>
    {children}
  </div>
);

const Notice = ({ title, children, tone = 'plain' }) => (
  <Shell>
    <div className="wc-container flex justify-center py-16">
      <div className={`w-full max-w-lg rounded-2xl border p-8 ${tone === 'success' ? 'border-success/30 bg-success-soft' : 'border-line bg-white'}`}>
        <h1 className="text-xl font-bold">{title}</h1>
        <div className="mt-3 space-y-3 text-sm text-muted">{children}</div>
      </div>
    </div>
  </Shell>
);

const Term = ({ label, value }) => (value ? (
  <div className="flex flex-wrap gap-x-4 border-b border-dashed border-line py-2 text-sm last:border-0">
    <dt className="w-full shrink-0 text-muted sm:w-44">{label}</dt>
    <dd className="flex-1 font-medium">{value}</dd>
  </div>
) : null);

const Sign = () => {
  const { reference } = useParams();
  const [state, setState] = useState({ phase: 'loading' });
  const [signedName, setSignedName] = useState('');
  const [address, setAddress] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [consent, setConsent] = useState(false);
  const [signature, setSignature] = useState(null); // { dataUrl, mode }
  const [modal, setModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api(`/sign/${encodeURIComponent(reference)}`)
      .then((res) => {
        if (cancelled) return;
        setState({ phase: res.data.ready ? 'ready' : 'notReady', data: res.data });
        setSignedName(res.data.candidate.fullName || '');
      })
      .catch((err) => { if (!cancelled) setState({ phase: 'error', error: err }); });
    return () => { cancelled = true; };
  }, [reference]);

  const data = state.data;

  // The server's text, with what the candidate has typed so far dropped in.
  // The signed PDF is built by the server from the submitted values.
  const markdown = useMemo(() => {
    if (!data) return '';
    let md = data.document.markdown;
    const original = data.candidate.fullName;
    const name = signedName.trim();
    if (name && original && name !== original) md = md.split(`**${original}**`).join(`**${name}**`);
    if (address.trim()) md = md.split(ADDRESS_GAP).join(address.trim());
    if (idNumber.trim()) md = md.replace(/(of \*\*[^*]+\*\*)( \(the \*\*Employee)/, `$1, identification number **${idNumber.trim()}**$2`);
    return md.split(DATE_GAP).join(fmtDate(new Date()));
  }, [data, signedName, address, idNumber]);

  if (state.phase === 'loading') {
    return <Shell><p className="wc-container py-16 text-muted">Loading your contract…</p></Shell>;
  }

  if (state.phase === 'error') {
    const { error } = state;
    if (error.status === 409 && error.code === 'alreadySigned') {
      return (
        <Notice title="This contract is already signed">
          <p>It was signed on {fmtDate(error.body.signedAt)}. Your signed copy was emailed to you and is in your placement portal.</p>
        </Notice>
      );
    }
    if (error.status === 410) {
      return (
        <Notice title="This contract has been withdrawn">
          <p>It can no longer be signed. Please check your placement portal for the latest update.</p>
        </Notice>
      );
    }
    if (error.status === 404) {
      return (
        <Notice title="This signing link isn't valid">
          <p>Please open it again from the Sign your contract button in your placement portal, rather than from a copied link.</p>
        </Notice>
      );
    }
    return (
      <Notice title="We couldn't load your contract">
        <p>{error.message}</p>
        <button type="button" className="wc-btn-primary" onClick={() => window.location.reload()}>Try again</button>
      </Notice>
    );
  }

  if (state.phase === 'notReady') {
    return (
      <Notice title="Your contract is nearly ready">
        <p>{COMPANY.tradingName} is still preparing your contract. Please try this link again later.</p>
        <p>Questions? Email <a className="text-primary underline" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.</p>
      </Notice>
    );
  }

  if (state.phase === 'done') {
    const first = signedName.trim().split(' ')[0];
    return (
      <Notice title="Your contract is signed" tone="success">
        <p className="text-ink">Thank you, {first}. Your employment contract with {data.employer.legalName} has been signed and saved.</p>
        {state.pdf && (
          <button type="button" className="wc-btn-primary" onClick={() => downloadBase64Pdf(state.pdf.base64, state.pdf.filename)}>
            Download your signed copy (PDF)
          </button>
        )}
        <p>We have also emailed you a copy, and it will appear in your placement portal. You can close this page.</p>
      </Notice>
    );
  }

  const t = data.terms || {};
  const canSubmit = signedName.trim().length >= 2 && address.trim().length >= 5 && consent && signature && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setErrors({});
    setFormError('');
    try {
      const res = await api(`/sign/${encodeURIComponent(reference)}`, {
        method: 'POST',
        body: {
          signedName: signedName.trim(),
          address: address.trim(),
          idNumber: idNumber.trim() || undefined,
          signatureDataUrl: signature.dataUrl,
          signatureMode: signature.mode,
          consent: true,
        },
      });
      setState((s) => ({ ...s, phase: 'done', pdf: { base64: res.data.pdfBase64, filename: res.data.pdfFilename } }));
      window.scrollTo(0, 0);
    } catch (err) {
      if (err.status === 409 || err.status === 410) { setState({ phase: 'error', error: err }); return; }
      const byField = {};
      (err.errors || []).forEach((e) => { byField[e.field] = e.message; });
      setErrors(byField);
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell>
      <div className="wc-container py-8 sm:py-12">
        <p className="wc-eyebrow">Employment contract</p>
        <h1 className="mt-2 text-2xl font-bold sm:text-3xl">{t.positionTitle || 'Your contract'} with {data.employer.legalName}</h1>
        <p className="mt-2 text-sm text-muted">Check your details, read the contract, then sign at the bottom of the page.</p>

        <div className="mt-8 grid items-start gap-6 lg:grid-cols-[1fr_340px]">
          <div className="space-y-6">
            <section className="wc-card p-5 sm:p-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-primary">Key terms</h2>
              <dl className="mt-3">
                <Term label="Position" value={t.positionTitle && `${t.positionTitle}${t.anzscoCode ? ` (ANZSCO ${t.anzscoCode})` : ''}`} />
                <Term label="Employment type" value={t.employmentType && `${sentence(t.employmentType)}${t.ordinaryHoursPerWeek ? `, ${t.ordinaryHoursPerWeek} ordinary hours per week` : ''}`} />
                <Term label="Annual base salary" value={fmtMoney(t.salaryAnnual)} />
                <Term label="Classification" value={t.classificationLevel && `Aged Care Award 2010, Direct Care Level ${t.classificationLevel}`} />
                <Term label="Start date" value={fmtDate(t.commencementDate)} />
                <Term label="Probation" value={t.probationMonths !== undefined && `${t.probationMonths} months`} />
                <Term label="Primary worksite" value={t.primaryWorksite} />
              </dl>
              <p className="mt-3 text-xs text-muted">A summary. The full contract below is what you sign.</p>
            </section>

            <section className="wc-card p-5 sm:p-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-primary">Your details</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="wc-label" htmlFor="name">Full name, as on your passport</label>
                  <input id="name" className="wc-input" value={signedName} onChange={(e) => setSignedName(e.target.value)} autoComplete="name" />
                  {errors.signedName && <p className="mt-1 text-xs text-danger">{errors.signedName}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className="wc-label" htmlFor="address">Current residential address</label>
                  <textarea id="address" rows={2} className="wc-input" value={address} onChange={(e) => setAddress(e.target.value)} autoComplete="street-address" />
                  {errors.address && <p className="mt-1 text-xs text-danger">{errors.address}</p>}
                </div>
                <div>
                  <label className="wc-label" htmlFor="idn">Passport number (optional)</label>
                  <input id="idn" className="wc-input" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} />
                  {errors.idNumber && <p className="mt-1 text-xs text-danger">{errors.idNumber}</p>}
                </div>
              </div>
            </section>

            <section className="wc-card p-5 sm:p-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-primary">The contract</h2>
              <p className="mt-1 text-xs text-muted">Your details above are filled in as you type. Your signed PDF will read exactly like this.</p>
              <div className="mt-4 max-h-[32rem] overflow-y-auto rounded-xl border border-line bg-white p-4 sm:p-6">
                <ContractMarkdown markdown={markdown} />
              </div>
              {data.schedules && data.schedules.length > 0 && (
                <div className="mt-4 text-sm">
                  <p className="font-semibold">Schedules to the contract</p>
                  <ul className="mt-1 space-y-1">
                    {data.schedules.map((s) => (
                      <li key={s.url}><a className="text-primary underline" href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</a></li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          </div>

          <aside className="wc-card p-5 sm:p-6 lg:sticky lg:top-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-primary">Sign</h2>

            {signature ? (
              <div className="mt-3 rounded-xl border border-line p-3">
                <img src={signature.dataUrl} alt="Your signature" className="max-h-20" />
                <button type="button" className="mt-2 text-xs text-primary underline" onClick={() => setModal(true)}>Change signature</button>
              </div>
            ) : (
              <button type="button" className="wc-btn-ghost mt-3 w-full" onClick={() => setModal(true)}>Add your signature</button>
            )}
            {errors.signature && <p className="mt-1 text-xs text-danger">{errors.signature}</p>}

            <label className="mt-4 flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>{data.consentText}</span>
            </label>
            {errors.consent && <p className="mt-1 text-xs text-danger">{errors.consent}</p>}

            {formError && <p className="mt-4 rounded-lg bg-danger-soft p-3 text-sm text-danger">{formError}</p>}

            <button type="button" className="wc-btn-primary mt-5 w-full" disabled={!canSubmit} onClick={submit}>
              {submitting ? 'Signing…' : 'Sign the contract'}
            </button>
            {!canSubmit && !submitting && (
              <p className="mt-2 text-xs text-muted">To sign: enter your name and address, add your signature and tick the box.</p>
            )}
            <p className="mt-4 text-xs text-muted">This link is personal to you. It stops working once the contract is signed.</p>
          </aside>
        </div>
      </div>

      {modal && (
        <SignatureModal
          fullName={signedName}
          onClose={() => setModal(false)}
          onApply={(dataUrl, mode) => { setSignature({ dataUrl, mode }); setModal(false); }}
        />
      )}
    </Shell>
  );
};

export default Sign;
